import React from 'react';
import { format } from 'date-fns';
import { CalendarClockIcon, MailIcon, PhoneIcon, PlusCircleIcon } from 'lucide-react';
import type { FollowUpLead } from '../data/followUps';
type FollowUpRowProps = {
  lead: FollowUpLead;
  campaigns: string[];
  onAction: (id: string, action: 'call' | 'email' | 'schedule') => void;
  onAssignCampaign: (id: string, campaignName: string) => void;
};
const statusTone: Record<FollowUpLead['status'], string> = {
  pending: 'bg-amber-50 text-amber-700',
  contacted: 'bg-success-soft text-success',
  snoozed: 'bg-gray-100 text-gray-600'
};
const statusLabel: Record<FollowUpLead['status'], string> = {
  pending: 'Pending',
  contacted: 'Contacted',
  snoozed: 'Snoozed'
};
const currency = (amount: number) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0
}).format(amount);
function IconAction({
  label,
  icon: Icon,
  onClick
}: {
  label: string;
  icon: React.ElementType;
  onClick: () => void;
}) {
  return <button type="button" onClick={onClick} title={label} aria-label={label} className="rounded-lg p-2 text-gray-500 transition-colors hover:bg-brand-soft hover:text-brand-dark focus:outline-none focus:ring-2 focus:ring-brand/30" data-id="element-299" data-mp="1tm6e2bmh8vpl">
      <Icon className="h-4 w-4" aria-hidden="true" data-id="element-300" data-mp="1plcqxkguzkxr" />
    </button>;
}
export function FollowUpRow({
  lead,
  campaigns,
  onAction,
  onAssignCampaign
}: FollowUpRowProps) {
  const overdue = new Date(lead.nextFollowUp).getTime() < Date.now();
  return <tr className="hover:bg-gray-50" data-id="element-301" data-mp="2hc0qis2vwi9b">
      <td className="px-5 py-3.5" data-id="element-302" data-mp="23lni6om2z1jg">
        <div className="font-semibold text-ink" data-id="element-303" data-mp="23km7il8z8h29">{lead.name}</div>
        <div className="text-[12px] text-gray-500" data-id="element-304" data-mp="28k8liwwmq1c4">{lead.email}</div>
        <div className="text-[12px] text-gray-500" data-id="element-305" data-mp="28idxh9q29t3t">{lead.phone}</div>
      </td>

      <td className="px-5 py-3.5" data-id="element-306" data-mp="2w8i47pyjq8z1">
        <div className="font-medium text-ink" data-id="element-307" data-mp="310t2kodn1mph">{currency(lead.loanAmount)}</div>
        <div className="text-[12px] text-gray-500" data-id="element-308" data-mp="312w2q18i8w7x">{lead.loanPurpose}</div>
      </td>

      <td className="px-5 py-3.5" data-id="element-309" data-mp="3aw0a2rnuh9n1">
        {lead.campaignName ? <div className="space-y-1" data-id="element-310" data-mp="35yqc2nme3pnl">
            <p className="max-w-[190px] truncate text-[12.5px] font-medium text-ink" data-id="element-311" data-mp="35vm0342onpmd">
              {lead.campaignName}
            </p>
            <span className="inline-flex items-center gap-1 rounded-full bg-navy-soft px-2 py-0.5 text-[11px] font-medium text-navy" data-id="element-312" data-mp="2vzr9nms7wq91">
              <MailIcon className="h-3 w-3" aria-hidden="true" data-id="element-313" data-mp="2ryp9n3qh8a60" />
              Email drip
            </span>
          </div> : <div className="max-w-[200px] space-y-1.5" data-id="element-314" data-mp="2n5co52h3gjhh">
            <p className="text-[12px] italic text-gray-400" data-id="element-315" data-mp="2i95ofrfak47z">Not in a campaign</p>
            <div className="relative" data-id="element-316" data-mp="2d9steje9nqub">
              <PlusCircleIcon className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-brand" aria-hidden="true" data-id="element-317" data-mp="2dauhbogds7tg" />
              <select aria-label={`Add ${lead.name} to a campaign`} value="" onChange={event => {
            if (event.target.value) onAssignCampaign(lead.id, event.target.value);
          }} className="w-full cursor-pointer rounded-md border border-brand/30 bg-brand-soft py-1.5 pl-8 pr-2 text-[12px] font-medium text-brand-dark outline-none transition-colors hover:bg-brand-soft/70 focus:ring-2 focus:ring-brand/30" data-id="element-318" data-mp="248uz4stmqxox">
                <option value="" data-id="element-319" data-mp="8eampu4fsm">Add to campaign…</option>
                {campaigns.map(name => <option key={name} value={name} data-id="element-320" data-mp="4zbmgg14324t">
                    {name}
                  </option>)}
              </select>
            </div>
          </div>}
      </td>

      <td className="px-5 py-3.5" data-id="element-321" data-mp="etakp3y2e1rh">
        <div className="text-[12.5px] text-ink" data-id="element-322" data-mp="3dhpuapy47b5d">
          {format(new Date(lead.lastContact), 'MMM d, yyyy')}
        </div>
        <div className="text-[11.5px] text-gray-500" data-id="element-323" data-mp="3if98xqyq5pxu">
          {format(new Date(lead.lastContact), 'h:mm a')}
        </div>
      </td>

      <td className="px-5 py-3.5" data-id="element-324" data-mp="1nxzr98m6hkmv">
        <div className="flex items-start gap-1.5" data-id="element-325" data-mp="1jz0onunx5zyl">
          <CalendarClockIcon className={['mt-0.5 h-3.5 w-3.5 shrink-0', overdue ? 'text-danger' : 'text-success'].join(' ')} aria-hidden="true" data-id="element-326" data-mp="1k0bq3ntw6vmj" />
          <div data-id="element-327" data-mp="1sxcehugyotpz">
            <div className={['text-[12.5px]', overdue ? 'text-danger' : 'text-ink'].join(' ')} data-id="element-328" data-mp="27ntcf3ue11i4">
              {format(new Date(lead.nextFollowUp), 'MMM d, yyyy')}
            </div>
            <div className="text-[11.5px] text-gray-500" data-id="element-329" data-mp="2gp9l975k0vk8">
              {format(new Date(lead.nextFollowUp), 'h:mm a')}
              {overdue ? ' · overdue' : ''}
            </div>
          </div>
        </div>
      </td>

      <td className="px-5 py-3.5" data-id="element-330" data-mp="mbruzcdrlkli">
        <span className={['inline-flex rounded-full px-2.5 py-1 text-[11px] font-medium', statusTone[lead.status]].join(' ')} data-id="element-331" data-mp="mct9jqtzop7z">
          {statusLabel[lead.status]}
        </span>
      </td>

      <td className="px-5 py-3.5" data-id="element-332" data-mp="2xybw38khnszt">
        <div className="flex items-center justify-end gap-1" data-id="element-333" data-mp="2xxk8ajx4uzk3">
          <IconAction label="Log call" icon={PhoneIcon} onClick={() => onAction(lead.id, 'call')} data-id="element-334" data-mp="2k7gw6lrywm8d" />
          <IconAction label="Send email" icon={MailIcon} onClick={() => onAction(lead.id, 'email')} data-id="element-335" data-mp="2o6fu3mfa7iqb" />
          <IconAction label="Set next follow-up" icon={CalendarClockIcon} onClick={() => onAction(lead.id, 'schedule')} data-id="element-336" data-mp="2f840dbj4c4tn" />
        </div>
      </td>
    </tr>;
}